import { concat } from '@ember/helper';
import { service } from '@ember/service';
import type { ValidatorMap } from 'ember-changeset/types';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import { modifier } from 'ember-modifier';
import CrudJournalEventBaseComponent from 'parog-web/components/forc/abstract/crud-journal-event-based-component';
import type { ToastMessage } from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import LinkToForc from 'parog-web/components/link-to-forc';
import WeightMeasuredJournalEvent from 'parog-web/models/weight-measured-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';
import BMI from 'parog-web/utils/bmi';
import formatNumberAsString from 'parog-web/utils/format-number-as-string';

export default class WeightEditionFormComponent extends CrudJournalEventBaseComponent<WeightMeasuredJournalEvent> {
  static override forcDisplayName = 'Mensurations';
  static override fullComponentId = 'measurements/weight-edition-form';
  static override journalEventClass = WeightMeasuredJournalEvent;

  @service declare flashMessages: FlashMessagesService;
  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;

  override journalEventProperties = ['occurredAt', 'weight', 'comments'];
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;

  get bmi(): number | undefined {
    if (!this.height) return;

    return BMI(this.changeset.get('weight'), this.height);
  }

  override get forcDisplayName(): string {
    return this.session.patient ? 'Mon poids' : super.forcDisplayName;
  }

  get height(): number | undefined {
    return this.args.patient.lastHeightMeasuredJournalEvent?.height;
  }

  override get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: 'Poids enregistré. Merci !',
          title: 'Poids'
        }
      : super.toastMessage;
  }

  override get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  focus = modifier(function (element: HTMLDivElement): void {
    (element.querySelector('.ui.input input') as HTMLInputElement).focus();
  });

  override async save(): Promise<void> {
    this.changeset.set(
      'weight',
      formatNumberAsString(this.changeset.get('weight'))
    );

    await super.save();
    this.analytics.trackPatientMeasurement('weight');
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <div class='two fields'>
          <div class='field'>
            <Form.Date
              @labelName='Date de la mesure'
              @propertyName='occurredAt'
            />
          </div>
          <div class='field' {{this.focus}}>
            <Form.NumberInput @labelName='Poids (kg)' @propertyName='weight' />
          </div>
        </div>

        {{#unless this.session.patient}}
          <div>
            {{if
              this.height
              (concat @patient.firstName ' ' @patient.lastName ' mesure ')
              'Pas de taille enregistrée'
            }}
            <LinkToForc
              @feature='measurements'
              @component='height-edition-form'
              @patient={{@patient}}
              data-tooltip='mettre à jour la taille'
            >
              {{if this.height (concat this.height 'cm.')}}
              <i class='pencil alternate icon'></i>
            </LinkToForc>
          </div>
          <div>
            {{if
              this.bmi
              (concat
                @patient.firstName
                ' '
                @patient.lastName
                ' a un IMC de : '
                this.bmi
              )
            }}
          </div>
        {{/unless}}
        <div class='field'>
          <Form.TextArea @labelName='Commentaires' @propertyName='comments' />
        </div>
      </:content>

    </Provider>
  </template>
}
